// Queue Management Service
// Manages queue system for accepted job applications and requests

import { db } from '../config/firebase';
import { ref, set, get, update } from 'firebase/database';

// Generate unique queue number
const generateQueueNumber = (type, date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.random().toString(36).substr(2, 9).toUpperCase();
  
  // Format: TYPE-YYYYMMDD-RANDOM (e.g., JA-20260325-ABC123XYZ)
  const typeCode = type === 'APPLICATION' ? 'JA' : 'JR';
  return `${typeCode}-${year}${month}${day}-${random}`;
};

// Create queue entry for accepted application
export const createApplicationQueue = async (userId, applicationId, jobTitle, userEmail) => {
  try {
    const queueNumber = generateQueueNumber('APPLICATION');
    
    const queueRef = ref(db, `queues/applications/${queueNumber}`);
    
    await set(queueRef, {
      queueNumber,
      userId,
      applicationId,
      jobTitle,
      userEmail,
      status: 'pending', // pending, proceeded, completed
      createdAt: new Date().toISOString(),
      proceedAt: null,
      completedAt: null,
      remarks: ''
    });

    // Also add to user profile
    const userQueueRef = ref(db, `users/${userId}/queues/applications/${queueNumber}`);
    await set(userQueueRef, {
      queueNumber,
      applicationId,
      jobTitle,
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    console.log('✅ Application queue created:', queueNumber);
    return queueNumber;
  } catch (error) {
    console.error('Error creating application queue:', error);
    throw error;
  }
};

// Create queue entry for accepted job request
export const createJobRequestQueue = async (userId, requestId, requestTitle, userEmail) => {
  try {
    const queueNumber = generateQueueNumber('JOB_REQUEST');
    
    const queueRef = ref(db, `queues/jobRequests/${queueNumber}`);
    
    await set(queueRef, {
      queueNumber,
      userId,
      requestId,
      requestTitle,
      userEmail,
      status: 'pending', // pending, proceeded, completed
      createdAt: new Date().toISOString(),
      proceedAt: null,
      completedAt: null,
      remarks: ''
    });

    // Also add to user profile
    const userQueueRef = ref(db, `users/${userId}/queues/jobRequests/${queueNumber}`);
    await set(userQueueRef, {
      queueNumber,
      requestId,
      requestTitle,
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    console.log('✅ Job request queue created:', queueNumber);
    return queueNumber;
  } catch (error) {
    console.error('Error creating job request queue:', error);
    throw error;
  }
};

// Get queue entry by number
export const getQueueEntry = async (queueNumber) => {
  try {
    let queueRef;

    // Determine type from queue number
    if (queueNumber.startsWith('JA')) {
      queueRef = ref(db, `queues/applications/${queueNumber}`);
    } else if (queueNumber.startsWith('JR')) {
      queueRef = ref(db, `queues/jobRequests/${queueNumber}`);
    } else {
      throw new Error('Invalid queue number format');
    }

    const snapshot = await get(queueRef);
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error fetching queue entry:', error);
    throw error;
  }
};

// Mark as proceeded to office
export const markAsProceeded = async (queueNumber) => {
  try {
    let queueRef;

    if (queueNumber.startsWith('JA')) {
      queueRef = ref(db, `queues/applications/${queueNumber}`);
    } else if (queueNumber.startsWith('JR')) {
      queueRef = ref(db, `queues/jobRequests/${queueNumber}`);
    } else {
      throw new Error('Invalid queue number format');
    }

    const queueEntry = await getQueueEntry(queueNumber);
    
    if (!queueEntry) {
      throw new Error('Queue entry not found');
    }

    await update(queueRef, {
      status: 'proceeded',
      proceedAt: new Date().toISOString()
    });

    // Update user's queue as well
    const userQueuePath = queueNumber.startsWith('JA')
      ? `users/${queueEntry.userId}/queues/applications/${queueNumber}`
      : `users/${queueEntry.userId}/queues/jobRequests/${queueNumber}`;

    const userQueueRef = ref(db, userQueuePath);
    await update(userQueueRef, {
      status: 'proceeded',
      proceedAt: new Date().toISOString()
    });

    console.log('✅ Queue entry marked as proceeded:', queueNumber);
    return true;
  } catch (error) {
    console.error('Error marking as proceeded:', error);
    throw error;
  }
};

// Get user's queue entries
export const getUserQueueEntries = async (userId, type = 'all') => {
  try {
    const entries = [];

    if (type === 'APPLICATION' || type === 'all') {
      const appQueueRef = ref(db, `users/${userId}/queues/applications`);
      const appSnapshot = await get(appQueueRef);
      if (appSnapshot.exists()) {
        entries.push(...Object.values(appSnapshot.val()).map(e => ({ ...e, type: 'APPLICATION' })));
      }
    }

    if (type === 'JOB_REQUEST' || type === 'all') {
      const jobQueueRef = ref(db, `users/${userId}/queues/jobRequests`);
      const jobSnapshot = await get(jobQueueRef);
      if (jobSnapshot.exists()) {
        entries.push(...Object.values(jobSnapshot.val()).map(e => ({ ...e, type: 'JOB_REQUEST' })));
      }
    }

    return entries;
  } catch (error) {
    console.error('Error fetching user queue entries:', error);
    return [];
  }
};

// Get statistics
export const getQueueStatistics = async () => {
  try {
    const appQueueRef = ref(db, 'queues/applications');
    const jobQueueRef = ref(db, 'queues/jobRequests');

    const appSnapshot = await get(appQueueRef);
    const jobSnapshot = await get(jobQueueRef);

    const appQueues = appSnapshot.exists() ? Object.values(appSnapshot.val()) : [];
    const jobQueues = jobSnapshot.exists() ? Object.values(jobSnapshot.val()) : [];

    const appPending = appQueues.filter(q => q.status === 'pending').length;
    const appProceeded = appQueues.filter(q => q.status === 'proceeded').length;
    
    const jobPending = jobQueues.filter(q => q.status === 'pending').length;
    const jobProceeded = jobQueues.filter(q => q.status === 'proceeded').length;

    return {
      applications: {
        total: appQueues.length,
        pending: appPending,
        proceeded: appProceeded
      },
      jobRequests: {
        total: jobQueues.length,
        pending: jobPending,
        proceeded: jobProceeded
      }
    };
  } catch (error) {
    console.error('Error fetching queue statistics:', error);
    return {
      applications: { total: 0, pending: 0, proceeded: 0 },
      jobRequests: { total: 0, pending: 0, proceeded: 0 }
    };
  }
};
